/**
 *
 * RegularAccordionComponent
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _isNumber from 'lodash/isNumber';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import ArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import Divider from 'components/Divider';
import PageIntroBlock from 'components/PageIntroBlock';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Mixpanel from 'utils/mixpanelService';
import BenchmarkSection from './BenchmarkSection';
import ResultList from './ResultList';
import RegularComponentLists from './Lists/RegularComponentLists';
import HorizontalProgress from './HorizontalProgress';
import TrendsWrapper from './Trends/TrendsWrapper';
import { getCurrentInterval, normalizeIntervals } from '../utils';

const useStyles = makeStyles(theme => ({
  regularComponentWrapper: {
    paddingTop: ({ invisiblePdfContent }) => (invisiblePdfContent ? 30 : 0),
    marginTop: ({ invisiblePdfContent }) => (invisiblePdfContent ? 0 : 16),
    marginBottom: 8,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  accordionLabel: {
    fontSize: 21,
  },
  score: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.palette.text.main,
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      fontSize: 18,
    },
  },
  chartWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  result: {
    fontSize: 18,
    textAlign: 'center',
  },
  resourcesContainer: {
    width: '100%',
    marginTop: 16,
    marginBottom: 16,
  },
  resultWrapper: {
    position: 'relative',
    width: '100%',
  },
  resultWrapperOpened: {
    overflow: 'inherit',
  },
  resultWrapperHidden: {
    maxHeight: 140,
    overflow: 'hidden',
  },
  readMore: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    margin: 0,
    height: 90,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: 8,
    background:
      'linear-gradient(to bottom, rgba(255, 255, 255, 0.2) 0%, white 40%, white)',
    cursor: 'pointer',
  },
  readMoreLabel: {
    color: '#01619B',
  },
  arrowIconWrapper: {
    width: 18,
    height: 18,
    border: '1px solid #01619B',
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowIconWrapperMargin: {
    marginLeft: 8,
  },
  arrowIcon: {
    fontSize: 14,
    color: '#01619B',
  },
  readLessWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 8,
  },
  readLess: {
    ...theme.typography.overline,
    padding: '4px 8px',
    color: '#01619B',
  },
}));

function RegularAccordionComponent({
  assessment,
  assessmentId,
  component,
  hideBar,
  isFullWidth,
  userData,
  withAccordion,
  invisiblePdfContent,
  linkAsPlainText,
  options,
}) {
  const {
    result,
    resources,
    topics,
    minValue,
    maxValue,
    scoring,
    isThermometer,
    intervals,
    resultExpression,
    name,
    scale,
    benchmark,
    benchmarkLabel,
    labelValue,
    reverseIntervals,
  } = component;

  const [isOpen, setOpen] = useState(false);
  const classes = useStyles({ invisiblePdfContent });
  const [assessmentResultsSiteCopy] = useSiteCopySelector([
    'assessment-results',
  ]);

  const displayTrendsForSubscores = _get(
    options,
    'displayTrendsForSubscores',
    false,
  );

  const sortedIntervals = intervals
    ? normalizeIntervals(
        intervals.sort((a, b) => a.start - b.start),
        minValue,
        maxValue,
        scale,
      )
    : {};

  let definitiveIntervals;
  if (intervals) {
    if (Array.isArray(intervals)) {
      definitiveIntervals = intervals;
    } else {
      definitiveIntervals = Object.keys(intervals).map(key => ({
        label: key,
        ...intervals[key],
      }));
    }
  }
  const [interval] = getCurrentInterval(definitiveIntervals, scoring);

  const handleReadMore = () => {
    Mixpanel.track('Assessment Results - Read More Clicked', {
      assessment: _get(assessment, 'fields.name'),
      component: name,
      entryId: assessmentId,
    });
    setOpen(true);
  };
  const handleReadLess = () => {
    Mixpanel.track('Assessment Results - Read Less Clicked', {
      assessment: _get(assessment, 'fields.name'),
      component: name,
      entryId: assessmentId,
    });
    setOpen(false);
  };

  const showChart =
    !hideBar &&
    _isNumber(maxValue) &&
    _isNumber(minValue) &&
    !_isEmpty(interval);

  let label;
  if (hideBar) {
    label = _get(result, 'fields.label');
  } else {
    label = resultExpression;
  }

  const lists = _get(result, 'fields.lists', []);
  const listsPosition = _get(result, 'fields.listPosition');
  const hasLists =
    (_isEmpty(listsPosition) ||
      listsPosition === 'Regular component list view') &&
    lists.length > 0;

  return (
    <Grid
      item
      xs={12}
      className={classes.regularComponentWrapper}
      id={`dynamic-id-${labelValue}`}
    >
      <Typography variant="h5" className={classes.accordionLabel} gutterBottom>
        {label}
      </Typography>
      {showChart && (
        <div className={classes.chartWrapper}>
          <HorizontalProgress
            isThermometer={isThermometer}
            label={label}
            min={minValue}
            max={maxValue}
            value={scoring}
            interval={interval}
            intervals={definitiveIntervals}
            reverse={reverseIntervals}
            scale={scale}
          />
        </div>
      )}
      <div
        className={_classNames({
          [classes.resultWrapper]: true,
          [classes.resultWrapperHidden]: withAccordion && !isOpen,
          [classes.resultWrapperOpened]: withAccordion && isOpen,
        })}
      >
        {!hideBar && _get(result, 'fields.label') && (
          <Typography className={classes.result}>
            {_get(result, 'fields.label')}
          </Typography>
        )}
        <PageIntroBlock
          id={_get(result, 'sys.id')}
          page="assessment-results"
          type="reference"
        />
        {displayTrendsForSubscores && (
          <Hidden smDown>
            <TrendsWrapper
              type="regular"
              component={component}
              assessment={assessment}
              slug={_get(assessment, 'fields.slug')}
              sortedIntervals={sortedIntervals}
              scale={scale}
            />
          </Hidden>
        )}
        <RichTextReactRenderer
          data={_get(result, 'fields.description')}
          paragraphVariant="body2"
          extraData={{
            benchmark: (
              <BenchmarkSection
                userData={userData}
                type={benchmark}
                text={benchmarkLabel}
              />
            ),
          }}
          linkAsPlainText={linkAsPlainText}
        />
        {displayTrendsForSubscores && (
          <Hidden mdUp>
            <TrendsWrapper
              type="regular"
              component={component}
              assessment={assessment}
              slug={_get(assessment, 'fields.slug')}
              sortedIntervals={sortedIntervals}
              scale={scale}
            />
          </Hidden>
        )}
        <Grid item xs={12} className={classes.resourcesContainer}>
          {!invisiblePdfContent &&
            (hasLists ? (
              <RegularComponentLists
                assessment={assessment}
                entryId={result.sys.id}
                listsStyle={_get(result, 'fields.listsStyle')}
                handPickedResources={_get(
                  result,
                  'fields.handPickedResources',
                  [],
                )}
                lists={_get(result, 'fields.lists', [])}
                topics={topics}
                isFullWidth={isFullWidth}
                labelValue={labelValue}
              />
            ) : (
              <ResultList
                assessment={_get(assessment, 'fields.name')}
                entryId={result.sys.id}
                resources={resources}
                topics={topics}
                isFullWidth={isFullWidth}
                labelValue={labelValue}
              />
            ))}
        </Grid>
        {withAccordion && !isOpen && (
          <div
            className={classes.readMore}
            onClick={handleReadMore}
            role="button"
            tabIndex={0}
          >
            <Typography className={classes.readMoreLabel} variant="overline">
              {_get(assessmentResultsSiteCopy, 'pageCopy.readMoreLabel')}
            </Typography>
            <div className={classes.arrowIconWrapper}>
              <ArrowDownIcon className={classes.arrowIcon} />
            </div>
          </div>
        )}
        {withAccordion && isOpen && (
          <Grid item xs={12} className={classes.readLessWrapper}>
            <ButtonBase
              disableRipple
              className={classes.readLess}
              onClick={handleReadLess}
            >
              {_get(assessmentResultsSiteCopy, 'pageCopy.readLessLabel')}
              <div
                className={_classNames(
                  classes.arrowIconWrapper,
                  classes.arrowIconWrapperMargin,
                )}
              >
                <ArrowUpIcon className={classes.arrowIcon} />
              </div>
            </ButtonBase>
          </Grid>
        )}
      </div>
      <Divider top={isFullWidth ? 32 : 8} bottom={8} width="100%" />
    </Grid>
  );
}

RegularAccordionComponent.propTypes = {
  assessment: PropTypes.object,
  assessmentId: PropTypes.string,
  component: PropTypes.object,
  hideBar: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  userData: PropTypes.shape({
    age: PropTypes.string,
    gender: PropTypes.string,
  }),
  withAccordion: PropTypes.bool,
};

export default RegularAccordionComponent;
